import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { Scissors, CalendarCheck, WhatsappLogo, CurrencyCircleDollar, ChartLineUp, Check, X, Calculator, Trophy } from "@phosphor-icons/react";
import { useLocale } from "@/i18n";
import FlagToggle from "@/components/FlagToggle";

export default function LandingPage() {
  const { t, setLocale } = useLocale();
  const { lang } = useParams();

  useEffect(() => {
    if (lang === "py") setLocale("es");
    else if (lang === "br") setLocale("pt");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  if (lang && !["br", "py"].includes(lang)) return <Navigate to="/" replace />;

  const features = [
    { icon: CalendarCheck, title: t("f1_t"), text: t("f1_d") },
    { icon: WhatsappLogo, title: t("f2_t"), text: t("f2_d") },
    { icon: CurrencyCircleDollar, title: t("f3_t"), text: t("f3_d") },
    { icon: ChartLineUp, title: t("f4_t"), text: t("f4_d") },
    { icon: Calculator, title: t("f5_t"), text: t("f5_d") },
    { icon: Trophy, title: t("f6_t"), text: t("f6_d") },
  ];
  const pains = [
    [t("pain1"), t("pain1_b")],
    [t("pain2"), t("pain2_b")],
    [t("pain3"), t("pain3_b")],
  ];
  const bullets = [t("plan_b1"), t("plan_b2"), t("plan_b3"), t("plan_b4"), t("plan_b5")];

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-50" data-testid="landing-page">
      <header className="w-full max-w-md mx-auto px-4 pt-6 flex items-center justify-between">
        <div className="flex items-center gap-2 text-amber-500">
          <Scissors size={22} weight="duotone" />
          <span className="font-display text-lg font-bold tracking-tight">Barber Menu</span>
        </div>
        <div className="flex items-center gap-4">
          <FlagToggle />
          <Link
            to="/entrar"
            data-testid="landing-login-link"
            className="text-sm text-zinc-400 hover:text-amber-500 transition-colors"
          >
            {t("enter")}
          </Link>
        </div>
      </header>

      <main className="w-full max-w-md mx-auto px-4">
        <section className="pt-14 pb-10 animate-fade-up">
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-amber-500">{t("landing_overline")}</p>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tighter leading-none mt-3">
            {t("landing_title")}
          </h1>
          <p className="text-base text-zinc-400 leading-relaxed mt-4">{t("landing_sub")}</p>
          <Link
            to="/cadastro"
            data-testid="landing-cta-btn"
            className="mt-8 inline-flex w-full items-center justify-center h-14 rounded-xl bg-amber-500 text-zinc-950 text-lg font-bold hover:bg-amber-400 transition-colors active:scale-95"
          >
            {t("landing_cta")}
          </Link>
          <p className="text-xs text-zinc-500 text-center mt-3">{t("landing_cta_sub")}</p>
        </section>

        <section className="pb-10" data-testid="landing-pain">
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
            <p className="text-xs uppercase tracking-[0.2em] font-semibold text-red-400">{t("pain_title")}</p>
            <ul className="mt-3 space-y-3 text-sm text-zinc-300">
              {pains.map(([pre, bold]) => (
                <li key={pre} className="flex gap-2.5">
                  <X size={16} weight="bold" className="text-red-400 shrink-0 mt-0.5" />
                  <span>{pre} <span className="font-bold text-zinc-100">{bold}</span></span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="space-y-4 pb-10" data-testid="landing-features">
          {features.map((f, i) => (
            <div
              key={f.title}
              style={{ animationDelay: `${i * 80}ms` }}
              className="animate-fade-up flex gap-4 bg-zinc-900 border border-zinc-800 rounded-xl p-4"
            >
              <f.icon size={26} weight="duotone" className="text-amber-500 shrink-0 mt-0.5" />
              <div>
                <p className="font-bold text-zinc-100">{f.title}</p>
                <p className="text-sm text-zinc-400 mt-1">{f.text}</p>
              </div>
            </div>
          ))}
        </section>

        <section className="pb-16" data-testid="landing-pricing">
          <div className="bg-zinc-900 border-2 border-amber-500 rounded-2xl p-6 text-center">
            <p className="text-xs uppercase tracking-[0.2em] font-semibold text-amber-500">{t("plan_label")}</p>
            <p className="mt-3">
              <span className="font-display text-5xl font-black text-zinc-50" data-testid="landing-price">{t("landing_price")}</span>
              <span className="text-zinc-400">{t("per_month")}</span>
            </p>
            <ul className="mt-5 space-y-2 text-sm text-zinc-300 text-left">
              {bullets.map((item) => (
                <li key={item} className="flex items-center gap-2">
                  <Check size={16} weight="bold" className="text-amber-500 shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
            <Link
              to="/cadastro"
              data-testid="pricing-cta-btn"
              className="mt-6 inline-flex w-full items-center justify-center h-12 rounded-xl bg-amber-500 text-zinc-950 font-bold hover:bg-amber-400 transition-colors active:scale-95"
            >
              {t("pricing_cta")}
            </Link>
            <p className="mt-3 text-xs text-emerald-400 font-semibold" data-testid="plan-trial-note">{t("plan_trial_note")}</p>
          </div>
          <p className="text-xs text-zinc-600 text-center mt-6">
            {t("roi")} <span className="text-zinc-400 font-semibold">{t("roi_bold")}</span>
          </p>
        </section>
      </main>
    </div>
  );
}
